package com.evoapps.evoshift.domain.usecase.todo

import com.evoapps.evoshift.domain.budget.GetMissingPayUseCase
import com.evoapps.evoshift.domain.budget.PayTermsChanges
import com.evoapps.evoshift.domain.budget.isUnfilledDemand
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.WorkHoursRules
import com.evoapps.evoshift.domain.model.needsClockReview
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.capacity.GetRosterCapacityUseCase
import com.evoapps.evoshift.domain.usecase.daily.FindMissingDailyResultsUseCase
import com.evoapps.evoshift.domain.usecase.requests.ObservePendingRequestsUseCase
import com.evoapps.evoshift.domain.usecase.requests.PendingRequests
import com.evoapps.evoshift.domain.usecase.roster.ObserveUnpublishedChangesUseCase
import com.evoapps.evoshift.domain.usecase.shift.clockReviewItems
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.plus

/**
 * SH-170: the "Do zrobienia" of an owner or a manager at one workplace, live: requests to decide
 * (the same ones the Requests badge counts), today's clock-ins to review, the last week's days
 * without takings, people working without pay terms, shifts nobody works in the next two weeks,
 * an availability request still missing answers before its deadline, a draft whose month
 * starts soon and (SH-263) a published month with changes not published yet. Each source only for
 * those whose [capabilities] include it (the same reads their screens make, so the rules allow
 * them); a source that fails to load is left out, never an error. Most urgent first
 * ([TodoRules.sorted]).
 */
class ObserveTeamTodoUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val pendingRequests: ObservePendingRequestsUseCase,
    private val findMissingTakings: FindMissingDailyResultsUseCase? = null,
    private val getMissingPay: GetMissingPayUseCase? = null,
    private val getCapacity: GetRosterCapacityUseCase? = null,
    private val payTermsChanges: PayTermsChanges? = null,
    private val today: () -> LocalDate = { AppClock.today() }
) {
    private val unpublishedChanges = ObserveUnpublishedChangesUseCase(shiftRepository, today)

    @OptIn(ExperimentalCoroutinesApi::class)
    operator fun invoke(workplaceId: String, capabilities: Set<Capability>): Flow<List<TodoItem>> {
        if (Capability.EDIT_ROSTER !in capabilities && Capability.APPROVE_REQUESTS !in capabilities) return flowOf(emptyList())
        val day = today()
        val requests: Flow<PendingRequests> =
            if (Capability.APPROVE_REQUESTS in capabilities) pendingRequests(workplaceId) else flowOf(PendingRequests())
        val rosters = rosterRepository.observeRosters(workplaceId).catch { emit(emptyList()) }
        return rosters.distinctUntilChanged().flatMapLatest { list ->
            // The months of the next two weeks (and today's, for its clock-ins).
            // SH-257: and yesterday's, for the hours still to confirm.
            val window = (-1 until TodoRules.UNFILLED_DAYS).map { day.plus(DatePeriod(days = it)) }
            val ids = window.map { RosterIds.of(workplaceId, it.year, it.month.number) }.distinct()
                .filter { id -> list.any { it.id == id } }
            val shifts: Flow<List<Shift>> = if (Capability.EDIT_ROSTER !in capabilities || ids.isEmpty()) flowOf(emptyList())
            else combine(ids.map { id -> shiftRepository.observeShiftsForRoster(id).catch { emit(emptyList()) } }) {
                it.toList().flatten()
            }
            // Read once per roster list (and again when pay terms change): the slower sources.
            // The first list comes once every source answered (requests start with none waiting).
            val slow = (payTermsChanges?.version ?: flowOf(0)).map { slowItems(workplaceId, capabilities, list, day) }
            // SH-263: published months with changes since, live (a publish clears them).
            val changes: Flow<List<TodoItem.RosterToPublish>> = if (Capability.EDIT_ROSTER !in capabilities) flowOf(emptyList())
            else unpublishedChanges(list).map { found ->
                found.values.sortedBy { it.year * 100 + it.month }
                    .map { TodoItem.RosterToPublish(it.rosterId, it.year, it.month, changes = it.count, firstChange = it.first) }
            }
            combine(requests, shifts, slow, changes) { pending, monthShifts, slower, changed ->
                val names = if (pending.absences.isEmpty()) emptyMap() else teamNames(workplaceId)
                val items = buildList {
                    requestsItem(pending, names)?.let(::add)
                    if (Capability.EDIT_ROSTER in capabilities) {
                        clockReviewItem(workplaceId, monthShifts, day)?.let(::add)
                        if (Capability.MAKE_CHANGES in capabilities) hoursItem(workplaceId, monthShifts, day)?.let(::add)
                        unfilledItem(monthShifts, day)?.let(::add)
                    }
                    addAll(slower)
                    // One item per month: a draft (in [slower]) is never also a published month.
                    addAll(changed.filter { item -> slower.none { it is TodoItem.RosterToPublish && it.rosterId == item.rosterId } })
                }
                TodoRules.sorted(items, day)
            }
        }.distinctUntilChanged()
    }

    private suspend fun teamNames(workplaceId: String): Map<String, String> =
        userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty().associate { it.uid to it.displayName }

    private fun requestsItem(pending: PendingRequests, names: Map<String, String>): TodoItem.PendingRequests? {
        if (pending.count == 0) return null
        // The request about the earliest day is the one named.
        val firstAbsence = pending.absences.minByOrNull { it.startDate }
        val firstSwap = pending.swaps.minByOrNull { it.date }
        val absenceFirst = firstAbsence != null && (firstSwap == null || firstAbsence.startDate <= firstSwap.date)
        val name = if (absenceFirst) names[firstAbsence!!.userId] else firstSwap?.swapRequest?.initiatorName
        val due = (if (absenceFirst) firstAbsence!!.startDate else firstSwap?.date)?.let { runCatching { LocalDate.parse(it.take(10)) }.getOrNull() }
        return TodoItem.PendingRequests(pending.absences.size, pending.swaps.size, name?.takeIf { it.isNotBlank() }, due)
    }

    private suspend fun clockReviewItem(workplaceId: String, shifts: List<Shift>, day: LocalDate): TodoItem.ClockReviews? {
        val todays = shifts.filter { it.date == day.toString() }
        if (todays.none { it.needsClockReview }) return null
        val settings = workplaceRepository.getWorkplace(workplaceId).getOrNull()?.settings?.clockReview ?: ClockReviewSettings()
        val count = clockReviewItems(todays, settings) { it }.size
        return if (count == 0) null else TodoItem.ClockReviews(count, day)
    }

    /** SH-257: yesterday's shifts whose hours wait for a manager (Godziny pracy). */
    private fun hoursItem(workplaceId: String, shifts: List<Shift>, day: LocalDate): TodoItem.HoursToConfirm? {
        val yesterday = day.minus(DatePeriod(days = 1))
        val count = WorkHoursRules.summary(shifts, yesterday.toString(), day.toString(), "${day}T00:00:00").toConfirm
        return if (count == 0) null else TodoItem.HoursToConfirm(workplaceId, yesterday, count)
    }

    private fun unfilledItem(shifts: List<Shift>, day: LocalDate): TodoItem.UnfilledShifts? {
        val last = day.plus(DatePeriod(days = TodoRules.UNFILLED_DAYS - 1)).toString()
        val open = shifts.filter { it.isUnfilledDemand && it.date >= day.toString() && it.date <= last }.sortedBy { it.plannedTime.start }
        val first = open.firstOrNull() ?: return null
        return TodoItem.UnfilledShifts(first.rosterId, open.size, LocalDate.parse(first.date))
    }

    private suspend fun slowItems(workplaceId: String, capabilities: Set<Capability>, rosters: List<Roster>, day: LocalDate): List<TodoItem> = buildList {
        val ids = rosters.map { it.id }.toSet()
        // SH-68: the takings of the week before today, for those who enter them.
        if (Capability.VIEW_FINANCES in capabilities && Capability.MAKE_CHANGES in capabilities) {
            val days = (TodoRules.TAKINGS_DAYS downTo 1).map { day.minus(DatePeriod(days = it)) }
            findMissingTakings?.invoke(workplaceId, ids, day, days)?.getOrNull()
                ?.mapNotNull { runCatching { LocalDate.parse(it.date) }.getOrNull() }
                ?.sorted()
                ?.takeIf { it.isNotEmpty() }
                ?.let { add(TodoItem.MissingTakings(workplaceId, it)) }
        }
        // SH-147: this month's people without pay terms (money roles only).
        val thisMonth = RosterIds.of(workplaceId, day.year, day.month.number)
        if (Capability.VIEW_FINANCES in capabilities && thisMonth in ids) {
            getMissingPay?.invoke(thisMonth)?.getOrNull()?.takeIf { it.people.isNotEmpty() }?.let { missing ->
                val first = missing.people.first()
                add(TodoItem.MissingPay(workplaceId, missing.count, first.userId, first.name))
            }
        }
        if (Capability.EDIT_ROSTER in capabilities) {
            rosters.filter { it.status == RosterStatus.DRAFT && startsWithin(it, day, TodoRules.PUBLISH_DAYS) && !ended(it, day) }
                .minByOrNull { it.year * 100 + it.month }
                ?.let { add(TodoItem.RosterToPublish(it.id, it.year, it.month)) }
        }
        if (Capability.REQUEST_AVAILABILITY in capabilities) {
            rosters.filter { it.availabilityRequestId != null && (it.status == RosterStatus.DRAFT || it.status == RosterStatus.SKELETON) }
                .sortedBy { it.year * 100 + it.month }
                .forEach { roster -> availabilityItem(roster, day)?.let(::add) }
        }
    }

    private suspend fun availabilityItem(roster: Roster, day: LocalDate): TodoItem.AvailabilityAnswers? {
        val capacity = getCapacity?.invoke(roster.id, readPayTerms = false)?.getOrNull() ?: return null
        val deadline = capacity.request?.dueDate?.take(10)?.let { runCatching { LocalDate.parse(it) }.getOrNull() }
        // After the deadline the manager plans with the answers there are.
        if (deadline != null && deadline < day) return null
        val missing = capacity.missing
        if (missing.isEmpty()) return null
        return TodoItem.AvailabilityAnswers(
            rosterId = roster.id,
            year = roster.year,
            month = roster.month,
            missing = missing.size,
            team = capacity.people.size,
            deadline = deadline,
            remindable = capacity.remindable.map { it.userId }
        )
    }

    private fun startsWithin(roster: Roster, day: LocalDate, days: Int): Boolean =
        LocalDate(roster.year, roster.month, 1) <= day.plus(DatePeriod(days = days))

    private fun ended(roster: Roster, day: LocalDate): Boolean =
        LocalDate(roster.year, roster.month, 1).plus(DatePeriod(months = 1)) <= day
}
